import { ModelType } from '@/pages/cvc/ModelControl/config';

/**
 * Runtime model catalog + runtime endpoint configuration.
 *
 * The CVC model list used to be derived from identity-provider permissions
 * (`/cvc/model/<name>`), which made the identity provider the only source of
 * truth for which models exist. The catalog replaces that with a JSON file
 * served alongside the static build, so the operating organisation can add or
 * remove models by editing one file — no rebuild, no identity provider.
 *
 * When the file is missing or malformed the built-in model list is used, so a
 * fresh deployment always renders a populated dropdown instead of an empty one.
 *
 * ISSUE-026: the SAME file optionally carries the API and WebSocket hosts
 * (`apiHost` / `socketHost`), so a deployed static bundle can be repointed at
 * a different backend by editing one file. A sibling config.json was rejected
 * deliberately: it would mean two boot-time round trips and two failure modes.
 * Omitted/invalid keys fall back to the compiled `REACT_APP_*` values, keeping
 * local development and the existing build pipeline unchanged.
 */

export const MODEL_CATALOG_FILE = 'models.json';

/**
 * A stalled request (black-holing proxy, captive portal, stuck CDN connection)
 * would otherwise hold the catalog in `loading` for the browser's full network
 * timeout, and the Select Model panel gates both the dropdown and the parameter
 * controls on that state — leaving the panel unusable with no retry short of a
 * reload. Abort well before that and fall back.
 */
export const MODEL_CATALOG_TIMEOUT_MS = 5000;

export type ModelCatalogStatus = 'loading' | 'ready' | 'fallback';

export interface ModelCatalogState {
  status: ModelCatalogStatus;
  models: ModelType[];
}

/**
 * Models compiled into the bundle, used when the catalog file is unusable.
 *
 * This deliberately mirrors the shipped `public/models.json` rather than
 * deriving from `MODEL_DESCRIPTIONS`: the description map covers every mode
 * the parameter panel supports (including modes the shipped catalog excludes),
 * and a missing catalog file must not quietly re-expose models the deployment
 * was curated to exclude.
 *
 * Keep this in step with `public/models.json` — a test asserts they match.
 */
export const FALLBACK_MODELS: ModelType[] = [
  'nansy-v2.8',
  'nansy-v2.7',
  'nansy-v2.5',
  'nansy-v2',
  'nansy-harmonic-token',
  'nansy-harmonic-token-interpolation',
  'nansy-v2.8-vdnet',
  'speaker-control',
];

export const INITIAL_MODEL_CATALOG: ModelCatalogState = {
  status: 'loading',
  models: [],
};

/**
 * Validate a parsed `models.json` payload.
 * Returns null when the payload cannot yield at least one usable model name,
 * which the caller treats as "fall back to the built-in list".
 */
export const parseModelCatalog = (raw: unknown): ModelType[] | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
    return null;

  const { cvc } = raw as { cvc?: unknown };
  if (!Array.isArray(cvc)) return null;

  const models: string[] = [];
  cvc.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const name = entry.trim();
    if (!name || models.includes(name)) return;
    models.push(name);
  });

  return models.length ? (models as ModelType[]) : null;
};

/**
 * Optional runtime endpoint hosts carried by `models.json` (ISSUE-026).
 * `undefined` means "the file does not override this host" — the caller falls
 * back to the compiled `REACT_APP_*` value.
 */
export interface RuntimeHosts {
  apiHost?: string;
  socketHost?: string;
}

/**
 * The fully-resolved boot configuration: the model catalog plus the API and
 * WebSocket hosts. The hosts are always concrete strings (runtime value or the
 * compiled env fallback) — consumers never see an undefined host.
 */
export interface RuntimeConfig {
  catalog: ModelCatalogState;
  apiHost: string;
  socketHost: string;
}

// Per-key scheme allowlists. Plaintext is permitted only outside production,
// keyed off the build mode rather than the hostname — hostname allowlists
// (e.g. "localhost") rot as soon as someone runs against a LAN address.
const SECURE_SCHEMES: Record<HostKey, string> = {
  apiHost: 'https:',
  socketHost: 'wss:',
};
const PLAINTEXT_SCHEMES: Record<HostKey, string> = {
  apiHost: 'http:',
  socketHost: 'ws:',
};

type HostKey = 'apiHost' | 'socketHost';

const allowsPlaintext = () => process.env.REACT_APP_ENV !== 'prd';

/**
 * Validate one host override from `models.json`.
 *
 * Checking only that the key is a non-empty string was not enough: a
 * scheme-less typo (`"apiHost": "api.example.com"`) became a *relative* axios
 * baseURL, so every API request 404'd against the page origin with nothing
 * pointing back at the config file. Validation therefore reaches into the
 * value — `new URL()` plus a per-key scheme allowlist — and a rejected value
 * returns undefined so the existing compiled-env fallback applies.
 */
const readHost = (value: unknown, key: HostKey): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const host = value.trim();
  if (!host) return undefined;

  const reject = (reason: string) => {
    console.warn(
      `[modelCatalog] ignoring invalid ${key} "${host}" (${reason}) — ` +
        'falling back to the compiled host'
    );
    return undefined;
  };

  let parsed: URL;
  try {
    parsed = new URL(host);
  } catch {
    // Covers "not a url", "//host" (no scheme), and other unparseable input.
    return reject('not an absolute URL');
  }

  const allowed = [SECURE_SCHEMES[key]];
  if (allowsPlaintext()) allowed.push(PLAINTEXT_SCHEMES[key]);

  if (!allowed.includes(parsed.protocol)) {
    return reject(
      parsed.protocol === PLAINTEXT_SCHEMES[key]
        ? 'plaintext is not allowed in a production build'
        : `scheme must be one of ${allowed.join(', ')}`
    );
  }

  return host;
};

/**
 * Extract the optional host overrides from a parsed `models.json` payload.
 * Invalid or empty values read as "not overridden" — never as an empty host.
 * Parsed independently of the model list so a malformed `cvc` array does not
 * discard valid hosts (and vice versa).
 */
export const parseRuntimeHosts = (raw: unknown): RuntimeHosts => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};

  const { apiHost, socketHost } = raw as {
    apiHost?: unknown;
    socketHost?: unknown;
  };
  return {
    apiHost: readHost(apiHost, 'apiHost'),
    socketHost: readHost(socketHost, 'socketHost'),
  };
};

// Read lazily (not at module scope) so the fallback reflects the env at
// resolution time — tests vary these values per case.
const envHosts = () => ({
  apiHost: process.env.REACT_APP_API_HOST as string,
  socketHost: process.env.REACT_APP_SOCKET_API_HOST as string,
});

// PUBLIC_URL keeps the request correct when the app is hosted under a sub-path.
const catalogUrl = () =>
  `${(process.env.PUBLIC_URL ?? '').replace(/\/$/, '')}/${MODEL_CATALOG_FILE}`;

/**
 * Fetch `models.json` once and resolve the catalog and the endpoint hosts.
 * Never rejects — every failure resolves to the built-in model list and the
 * compiled env hosts, so a boot-time call cannot produce an unhandled
 * rejection and no consumer ever receives an undefined host.
 */
export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MODEL_CATALOG_TIMEOUT_MS);

  try {
    const response = await fetch(catalogUrl(), {
      cache: 'no-cache',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`${MODEL_CATALOG_FILE} responded ${response.status}`);
    }

    const raw: unknown = await response.json();
    const models = parseModelCatalog(raw);
    const hosts = parseRuntimeHosts(raw);
    if (!models) {
      console.warn(
        `[modelCatalog] using the built-in model list — ${MODEL_CATALOG_FILE} has an unexpected shape`
      );
    }

    return {
      catalog: models
        ? { status: 'ready', models }
        : { status: 'fallback', models: FALLBACK_MODELS },
      apiHost: hosts.apiHost ?? envHosts().apiHost,
      socketHost: hosts.socketHost ?? envHosts().socketHost,
    };
  } catch (error) {
    console.warn(
      `[modelCatalog] using built-in fallbacks — could not load ${MODEL_CATALOG_FILE}`,
      error
    );
    return {
      catalog: { status: 'fallback', models: FALLBACK_MODELS },
      ...envHosts(),
    };
  } finally {
    clearTimeout(timer);
  }
};

// Single-flight cache: the catalog hook, the axios interceptor, and the
// WebSocket provider all await the same boot-time fetch — one round trip.
let runtimeConfigPromise: Promise<RuntimeConfig> | null = null;

export const getRuntimeConfig = (): Promise<RuntimeConfig> => {
  if (!runtimeConfigPromise) runtimeConfigPromise = loadRuntimeConfig();
  return runtimeConfigPromise;
};

/** Test-only: clear the single-flight cache between test cases. */
export const resetRuntimeConfigForTests = (): void => {
  runtimeConfigPromise = null;
};

/**
 * Fetch the catalog once at boot. Never rejects — every failure resolves to the
 * fallback list so a boot-time call cannot produce an unhandled rejection.
 */
export const loadModelCatalog = async (): Promise<ModelCatalogState> =>
  (await loadRuntimeConfig()).catalog;
